import { createAsyncThunk } from '@reduxjs/toolkit';

import { ISubmission } from '.';
import { UserApiPaths } from '../../dataProvider/APIPaths';
import { IQuestion } from '../QuestionSlice';
import APIDataProvider from '../../dataProvider/APIDataProvider';
import { ISubTopic } from '../SubTopicSlice';
import { IQuiz } from '../QuizSlice';

export interface IGetAllSubmissions {
  limit?: number;
  offset?: number;
  quizId: string;
}
export interface ICreateNewSubmission {
  subTopicId: string;
  paper: string;
}
export interface ICreateNewSubmissionByQuizId {
  subTopicId: string;
  quizId: string;
  homeworkId?: string;
}
export interface ISubmissionCreateAnswer {
  submissionId: string;
  questionId: string;
  answerId: string;
}
export interface ISubmissionFinish {
  submissionId: string;
}
export interface ISubmissionGetQuestion {
  submissionId: string;
  questionId: string;
}
export interface ISubmissionGetQuestionAnswer {
  submissionId: string;
  questionId: string;
}
export interface IGetSubmissionsQuizHistory {
  limit: number;
  offset: number;
  quizId: string;
}

export interface ISubmissionUpdateCountdownSettings {
  rememberOption: boolean;
  submissionId: string;
  isContinueSubmissionWithTimerEnabled: boolean;
}

export interface IUpdateQuestionTimer {
  submissionId: string;
  questionId: string;
}

export interface IUpdateSubmissionQuestionOpenAiFeedback {
  submissionId: string;
  questionId: string;
  feedback: -1 | 0 | 1;
}

export interface ISubmissionCreateOpenEndedAnswer {
  submissionId: string;
  questionId: string;
  openEndedAnswer: string;
}

export interface ISubmissionGradeOpenEndedAnswer {
  submissionId: string;
  questionId: string;
  score?: number;
  comment?: string;
}

export interface IGetAiHint {
  questionId: string;
}

const CreateSubmissionApiDataProvider = new APIDataProvider<{
  submission: ISubmission;
  subTopic: ISubTopic;
}>();
const SubmissionApiDataProvider = new APIDataProvider<ISubmission>();
const QuestionApiDataProvider = new APIDataProvider<IQuestion>();
const AIHintDataProvider = new APIDataProvider<{
  questionId: string;
  aiHint: string;
}>();
const SubmissionCountdownSettingsApiDataProvider = new APIDataProvider<{
  isTimerEnabled: boolean;
  rememberOption: boolean;
  timerStart?: number;
}>();

const QuestionStartTimerApiDataProvider = new APIDataProvider<{
  submissionId?: string;
  questionId?: string;
  timerStart?: number;
}>();

const QuestionEndTimerApiDataProvider = new APIDataProvider<{
  submissionId?: string;
  questionId?: string;
  timerEnd?: number;
}>();

const GetQuizDataForAboutPageDataProvider = new APIDataProvider<{
  list: Array<{ paper: string; quiz: IQuiz }>;
}>();

const SubmissionQuestionOpenAiFeedbackDataProvider = new APIDataProvider<IUpdateSubmissionQuestionOpenAiFeedback>();

const getSubmissionListModule = async (data: IGetAllSubmissions) => {
  let path = `${UserApiPaths.submissions}?quizId=${data.quizId}`;
  if (data.limit) {
    path += `&limit=${data.limit}`;
  }
  if (data.offset) {
    path += `&offset=${data.offset}`;
  }
  const response = await SubmissionApiDataProvider.get(path);
  return response.data as {
    limit: number;
    offset: number;
    totalDocs: number;
    docs: Array<ISubmission>;
  };
};

const getSubmissionsForQuizHistoryListModule = async (
  data: IGetSubmissionsQuizHistory
) => {
  const _path = `${UserApiPaths.submissions}/quizHistory/${data.quizId}?quizId=${data.quizId}&limit=${data.limit}&offset=${data.offset}`;
  const response = await SubmissionApiDataProvider.get(_path);
  return response.data as {
    limit: number;
    offset: number;
    totalDocs: number;
    docs: Array<ISubmission>;
  };
};

export const getSubmissionList = createAsyncThunk(
  'user/submission/list',
  getSubmissionListModule
);

export const loadMoreSubmissionList = createAsyncThunk(
  'user/submission/loadMore',
  getSubmissionListModule
);

const createSubmissionApi = async (data: ICreateNewSubmission) => {
  const response = await CreateSubmissionApiDataProvider.create(
    UserApiPaths.submissions,
    data
  );

  return response.data;
};

export const createNewSubmissionBySubTopic = createAsyncThunk(
  'user/submission/subTopic/createNewSubmissionBySubTopic',
  createSubmissionApi
);

export const createNewSubmissionByQuizId = createAsyncThunk(
  'user/submission/createNewSubmissionByQuizId',
  async (data: ICreateNewSubmissionByQuizId) => {
    const response = await CreateSubmissionApiDataProvider.create(
      `${UserApiPaths.submissions}/createByQuizId`,
      data
    );

    return response.data;
  }
);

export const getSubmissionById = createAsyncThunk(
  'user/submission/id',
  async (id: string) => {
    const response = await SubmissionApiDataProvider.getById(
      `${UserApiPaths.submissions}/${id}`
    );

    return response.data;
  }
);

export const createSubmissionAnswer = createAsyncThunk(
  'user/submission/create/answer',
  async (data: ISubmissionCreateAnswer) => {
    const response = await SubmissionApiDataProvider.create(
      `${UserApiPaths.submissions}/answer/add`,
      data
    );
    return response.data;
  }
);

export const finishSubmission = createAsyncThunk(
  'user/submission/finish',
  async (data: ISubmissionFinish) => {
    const response = await SubmissionApiDataProvider.create(
      `${UserApiPaths.submissions}/finish`,
      data
    );
    return response.data;
  }
);

export const getSubmissionQuestion = createAsyncThunk(
  'user/submission/question/get',
  async (data: ISubmissionGetQuestion) => {
    const response = await QuestionApiDataProvider.create(
      `${UserApiPaths.submissions}/question/get`,
      data
    );
    return response.data;
  }
);

export const showSubmissionQuestionAnswer = createAsyncThunk(
  'user/submission/question/answer/get',
  async (data: ISubmissionGetQuestionAnswer) => {
    const response = await QuestionApiDataProvider.create(
      `${UserApiPaths.submissions}/answer/get`,
      data
    );
    return response.data;
  }
);

export const showAiHint = createAsyncThunk(
  'user/submission/question/aiHint',
  async (data: IGetAiHint) => {
    const response = await AIHintDataProvider.create(
      `${UserApiPaths.submissions}/aiHint`,
      data
    );
    return response.data;
  }
);

export const getSubmissionsForQuizHistory = createAsyncThunk(
  'user/submission/quizHistory',
  getSubmissionsForQuizHistoryListModule
);

export const loadMoreSubmissionsForQuizHistory = createAsyncThunk(
  'user/submission/quizHistory/loadMore',
  getSubmissionsForQuizHistoryListModule
);

export const updateSubmissionCountdownSettings = createAsyncThunk(
  'user/submission/updateCountDownSettings',
  async (data: ISubmissionUpdateCountdownSettings) => {
    const response = await SubmissionCountdownSettingsApiDataProvider.create(
      `${UserApiPaths.submissions}/updateSubmissionCountdownSettings`,
      data
    );

    return response.data;
  }
);

export const startSubmissionQuestionTimer = createAsyncThunk(
  'user/submission/startSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await QuestionStartTimerApiDataProvider.create(
      `${UserApiPaths.submissions}/startSubmissionQuestionTimer`,
      data
    );

    return response.data;
  }
);

export const endSubmissionQuestionTimer = createAsyncThunk(
  'user/submission/endSubmissionQuestionTimer',
  async (data: IUpdateQuestionTimer) => {
    const response = await QuestionEndTimerApiDataProvider.create(
      `${UserApiPaths.submissions}/endSubmissionQuestionTimer`,
      data
    );

    return response.data;
  }
);

export const updateSubmissionQuestionOpenAiFeedback = createAsyncThunk(
  'user/submission/updateQuestionOpenAiFeedback',
  async (data: IUpdateSubmissionQuestionOpenAiFeedback) => {
    const response = await SubmissionQuestionOpenAiFeedbackDataProvider.create(
      `${UserApiPaths.submissions}/updateQuestionOpenAiFeedback`,
      data
    );

    return response.data;
  }
);

export const createSubmissionOpenEndedAnswer = createAsyncThunk(
  'user/submission/create/openEndedAnswer',
  async (data: ISubmissionCreateOpenEndedAnswer) => {
    const response = await SubmissionApiDataProvider.create(
      `${UserApiPaths.submissions}/openEndedAnswer/add`,
      data
    );
    return response.data;
  }
);

export const gradingSubmissionOpenEndedAnswer = createAsyncThunk(
  'user/submission/grade/openEndedAnswer',
  async (data: ISubmissionGradeOpenEndedAnswer) => {
    const response = await SubmissionApiDataProvider.create(
      `${UserApiPaths.submissions}/openEndedAnswer/grade`,
      data
    );
    return response.data;
  }
);

export const getQuizDataForAboutPage = createAsyncThunk(
  'user/submission/getQuizDataForAboutPage',
  async (data: { subTopicId: string }) => {
    const response = await GetQuizDataForAboutPageDataProvider.create(
      `${UserApiPaths.submissions}/getQuizDataForAboutPage`,
      data
    );
    return response.data;
  }
);
